import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import CustomMultiSelect from '@/components/CustomComponent/CustomMultiSelect/CustomMultiSelect';
import { COMMON_TEXT } from '@/helpers/common-text';
import { Form, Formik, getIn } from 'formik';
import { cloneDeep, isEqual } from 'lodash';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { UsersApi } from '@/openapi';
import { createAuthenticatedApiClient } from 'src/helpers/api-client';
import './UserInfoDialog.less';

const USER_ROW_STATUS = {
  UNCHANGED: 0,
  NEW: 1,
  MODIFIED: 2,
  DELETED: 3,
};

const USER_PRIVILEGE = {
  STANDARD: 'standard',
  SIGNAGE: 'signage',
  ADMIN: 'admin',
};

const PRIVILEGE_OPTIONS = [
  { label: '標準', value: USER_PRIVILEGE.STANDARD },
  { label: 'サイネージ', value: USER_PRIVILEGE.SIGNAGE },
  { label: '管理者', value: USER_PRIVILEGE.ADMIN },
];

const AUTH_METHOD_OPTIONS = [
  { label: 'パスワード', value: 'password' },
  { label: 'シングルサインオン', value: 'sso' },
];

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class UserInfoDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tenantId: props.tenantId,
      selectedUser: null,
      isLoadingUser: true,
      dialogVisibleCount: 0,
      isSubmitting: false,
      currentFormValues: null,
    };
    this.formikRef = React.createRef();
    this.toastRef = React.createRef();
  }

  componentDidMount() { }

  componentDidUpdate(prevProps) {
    const { dialogVisibleCount: count, selectedUser, tenantId } = this.props;
    const { dialogVisibleCount: prevCount } = prevProps;

    if (count > 0 && count !== prevCount) {
      if (selectedUser) {
        this.setState({
          tenantId,
          dialogVisibleCount: count,
          selectedUser: {
            ...selectedUser,
            username: selectedUser.username || '',
            firstname: selectedUser.firstname || '',
            lastname: selectedUser.lastname || '',
            privilege: selectedUser.privilege || '',
            password: selectedUser.password || '',
            confirmPassword: selectedUser.confirmPassword || '',
            isOverrideLoginMethod: selectedUser.isOverrideLoginMethod || false,
            loginMethods: selectedUser.loginMethods || [],
            status: selectedUser.status || USER_ROW_STATUS.UNCHANGED
          }
        });
      } else {
        this.setState({
          tenantId,
          dialogVisibleCount: count,
          selectedUser: null
        });
      }
    }
  }

  formSchema = Yup.object().shape({
    user: Yup.object().shape({
      username: Yup.string().email(COMMON_TEXT.EMAIL_FORMAT_ERROR_MESSAGE).required(COMMON_TEXT.REQUIRED),
      firstname: Yup.string().required(COMMON_TEXT.REQUIRED),
      lastname: Yup.string().required(COMMON_TEXT.REQUIRED),
      privilege: Yup.string().required(COMMON_TEXT.REQUIRED),
    }),
  });

  isFormFieldInvalid = ({ name, touched, errors }) => {
    const error = getIn(errors, name);
    return error ? 'p-invalid' : '';
  };

  getFormErrorMessage = ({ name, touched, errors }) => {
    return this.isFormFieldInvalid({ name, touched, errors }) ? (
      <span className="validation-error-message">{getIn(errors, name)}</span>
    ) : (
      <></>
    );
  };

  fetchUserData = async ({ username, rowIndexId }) => {
    try {
      const usersApi = createAuthenticatedApiClient(UsersApi);
      const response = await usersApi.getUserByEmail({ email: username });

      // Parse login_methods string to array
      let loginMethodsArray = [];
      if (response.loginMethods) {
        loginMethodsArray = response.loginMethods.split(',').map(m => m.trim());
      }

      const user = {
        id: response.id,
        firstname: response.firstname,
        lastname: response.lastname,
        privilege: response.privilege,
        username: response.userName,
        tenant_id: response.tenantId,
        password: '',
        confirmPassword: '',
        isOverrideLoginMethod: response.isOverrideLoginMethod || false,
        loginMethods: loginMethodsArray,
        rowIndexId,
      };
      return {
        ...user,
        initUser: cloneDeep(user),
      };
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      this.toastRef.current.show({
        severity: 'error',
        summary: COMMON_TEXT.UPLOAD_ERROR_MESSAGE,
        detail: COMMON_TEXT.UPLOAD_MULTIPLE_ERROR_MESSAGE,
      });
      return null;
    }
  };

  userDialogOnShow = async () => {
    const { selectedUser } = this.state;
    const { status, username, rowIndexId } = selectedUser ?? {};

    if (status === USER_ROW_STATUS.UNCHANGED) {
      this.setState({ isLoadingUser: true });
      const user = await this.fetchUserData({ username, rowIndexId });
      this.setState({
        selectedUser: user,
        isLoadingUser: false,
      });
    } else {
      this.setState({ isLoadingUser: false });
    }
  };

  isUserInfoModified = ({ user, initUser }) => {
    const initKeys = Object.keys(initUser);
    for (let i = 0; i < initKeys.length; i++) {
      const key = initKeys[i];
      if (!isEqual(user[key] ?? '', initUser[key] ?? '')) {
        return true;
      }
    }
    return false;
  };

  updateSelectedUser = async () => {
    if (this.formikRef.current) {
      this.setState({ isSubmitting: true }, () => {
        this.formikRef.current.validateForm().then(errors => {
          if (errors['user']) {
            this.setState({ isSubmitting: false });
            return;
          }
          const { user } = this.formikRef.current.values;
          const { initUser } = user;

          // Create a copy for API submission with loginMethods converted to string
          const userForApi = {
            ...user,
            tenantId: this.props.tenantId,
            loginMethods: user.loginMethods
          };

          if (user.status === USER_ROW_STATUS.NEW) {
            // Generate random password for new users
            const generatedPassword = this.generatePassword();
            userForApi.password = generatedPassword;
            userForApi.confirmPassword = generatedPassword;
            this.props.updateUserInfo({ user: userForApi, userIndex: -1 });
          } else {
            if (this.isUserInfoModified({ user, initUser })) {
              userForApi.status = USER_ROW_STATUS.MODIFIED;
            } else {
              userForApi.status = USER_ROW_STATUS.UNCHANGED;
            }
            this.props.updateUserInfo({ user: userForApi });
          }
          this.setState({ isSubmitting: false });
          this.hideDialog();
        });
      });
    }
  };

  userDialogFooter = () => {
    const { isLoadingUser, currentFormValues } = this.state;

    // Check if save button should be disabled
    let isSaveDisabled = isLoadingUser;

    // Additional validation: if override login method is enabled but no methods selected
    if (currentFormValues?.user) {
      const { isOverrideLoginMethod, loginMethods } = currentFormValues.user;
      if (isOverrideLoginMethod && (!loginMethods || loginMethods.length === 0)) {
        isSaveDisabled = true;
      }
    }

    return (
      <div className="dialog-footer-center">
        <Button
          type="button"
          label={COMMON_TEXT.SAVE}
          className="add-button"
          disabled={isSaveDisabled}
          onClick={e => this.updateSelectedUser(e)}
        />
      </div>
    );
  };

  hideDialog = () => {
    this.setState({
      selectedUser: null,
      isLoadingUser: true,
      dialogVisibleCount: 0,
      isSubmitting: false,
    });
  };

  generatePassword = () => {
    const length = 12;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*(),.?":{}|<>';

    // Ensure at least one character from each required category
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];

    // Fill the rest with random characters from all categories
    const allChars = uppercase + lowercase + numbers + special;
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password to randomize the order
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    return password;
  };

  render() {
    const { selectedUser, isLoadingUser, dialogVisibleCount } = this.state;
    return (
      <div className="user-info-dialog-wrapper">
        <Toast ref={this.toastRef} position="top-center" />
        <Dialog
          visible={dialogVisibleCount > 0}
          style={{ width: '500px' }}
          breakpoints={{ '960px': '75vw', '641px': '90vw' }}
          header={COMMON_TEXT.USER_INFORMATION}
          modal
          className="p-fluid user-setting-info-dialog"
          footer={this.userDialogFooter}
          onHide={this.hideDialog}
          onShow={this.userDialogOnShow}
          appendTo={'self'}
          resizable={false}
        >
          <div className="user-info-container">
            {selectedUser?.id && isLoadingUser ? (
              <LoadingSpinner />
            ) : !selectedUser ? (
              <>NO_DATA</>
            ) : (
              <Formik
                innerRef={this.formikRef}
                initialValues={{ user: selectedUser }}
                enableReinitialize={true}
                validationSchema={this.formSchema}
                validate={values => {
                  const errors = {};
                  const { user } = values;
                  const { userList = [] } = this.props;
                  const { isSubmitting } = this.state;
                  // Only check for new users
                  if (isSubmitting && user.status === USER_ROW_STATUS.NEW) {
                    const emailExists = userList.some(
                      u => u.username === user.username && u.rowIndexId !== user.rowIndexId && u.status !== USER_ROW_STATUS.DELETED
                    );
                    if (emailExists) {
                      errors.user = errors.user || {};
                      errors.user.username = COMMON_TEXT.EMAIL_ALREADY_EXISTS;
                    }
                  }
                  return errors;
                }}
                onSubmit={values => { }}
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  touched,
                  errors,
                }) => {
                  // Track form values for footer button state
                  if (this.state.currentFormValues !== values) {
                    // Use setTimeout to avoid setState during render
                    setTimeout(() => {
                      if (this.state.currentFormValues !== values) {
                        this.setState({ currentFormValues: values });
                      }
                    }, 0);
                  }

                  return (
                    <Form>
                      <div className="user-form-layout">
                        <div className="field-row">
                          <div className="field-label">
                            <span className="figma-label-style">
                              {COMMON_TEXT.EMPLOYEE_EMAIL}
                            </span>
                          </div>
                          <div className="field-input">
                            <div className="input-text-field-container">
                              <InputText
                                id={`user.username`}
                                value={values.user.username}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={values.user.status !== USER_ROW_STATUS.NEW}
                                className={`${this.isFormFieldInvalid({
                                  name: `user.username`,
                                  touched,
                                  errors,
                                })}`}
                              />
                              {this.getFormErrorMessage({
                                name: `user.username`,
                                touched,
                                errors,
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="field-row">
                          <div className="field-label">
                            <span className="figma-label-style">
                              {COMMON_TEXT.LAST_NAME}
                            </span>
                          </div>
                          <div className="field-input">
                            <div className="input-text-field-container">
                              <InputText
                                id={`user.lastname`}
                                value={values.user.lastname}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`${this.isFormFieldInvalid({
                                  name: `user.lastname`,
                                  touched,
                                  errors,
                                })}`}
                              />
                              {this.getFormErrorMessage({
                                name: `user.lastname`,
                                touched,
                                errors,
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="field-row">
                          <div className="field-label">
                            <span className="figma-label-style">
                              {COMMON_TEXT.FIRST_NAME}
                            </span>
                          </div>
                          <div className="field-input">
                            <div className="input-text-field-container">
                              <InputText
                                id={`user.firstname`}
                                value={values.user.firstname}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`${this.isFormFieldInvalid({
                                  name: `user.firstname`,
                                  touched,
                                  errors,
                                })}`}
                              />
                              {this.getFormErrorMessage({
                                name: `user.firstname`,
                                touched,
                                errors,
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="field-row">
                          <div className="field-label">
                            <span className="figma-label-style">
                              {COMMON_TEXT.PRIVILEGE}
                            </span>
                          </div>
                          <div className="field-input">
                            <div className="figma-dropdown">
                              <Dropdown
                                id={`user.privilege`}
                                value={values.user.privilege}
                                options={PRIVILEGE_OPTIONS}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`${this.isFormFieldInvalid({
                                  name: `user.privilege`,
                                  touched,
                                  errors,
                                })}`}
                              />
                              {this.getFormErrorMessage({
                                name: `user.privilege`,
                                touched,
                                errors,
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Authentication Override Section */}
                        <div className="field-row">
                          <div className="field-label">
                            <span className="figma-label-style">
                              {COMMON_TEXT.OVERRIDE_LOGIN_METHOD_LABEL}
                            </span>
                          </div>
                          <div className="field-input">
                            <div className="toggle-container">
                              <InputSwitch
                                inputId="user.isOverrideLoginMethod"
                                name="user.isOverrideLoginMethod"
                                checked={values.user.isOverrideLoginMethod}
                                onChange={(e) => {
                                  handleChange({
                                    target: {
                                      name: 'user.isOverrideLoginMethod',
                                      value: e.value,
                                    },
                                  });
                                  // Clear login methods if turning off
                                  if (!e.value) {
                                    handleChange({
                                      target: {
                                        name: 'user.loginMethods',
                                        value: [],
                                      },
                                    });
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Login Methods MultiSelect (conditional) */}
                        {values.user.isOverrideLoginMethod && (
                          <div className="field-row">
                            <div className="field-label">
                              <span className="figma-label-style">
                                {COMMON_TEXT.LOGIN_METHODS_LABEL}
                              </span>
                            </div>
                            <div className="field-input multiselect-wrap">
                              <CustomMultiSelect
                                hideLabel={true}
                                label={COMMON_TEXT.LOGIN_METHODS_LABEL}
                                value={values.user.loginMethods}
                                options={AUTH_METHOD_OPTIONS}
                                onChange={(e) => {
                                  handleChange({
                                    target: {
                                      name: 'user.loginMethods',
                                      value: e.value,
                                    },
                                  });
                                }}
                                placeholder={COMMON_TEXT.SELECT_LOGIN_METHODS}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            )}
          </div>
        </Dialog>
      </div>
    );
  }
}

UserInfoDialog.propTypes = {
  selectedUser: PropTypes.object,
  dialogVisibleCount: PropTypes.number,
  tenantId: PropTypes.number,
  updateUserInfo: PropTypes.func.isRequired,
};

export default UserInfoDialog;
